/**
 * RelationsReviewStep — review CampaignRelation[] from wave 3.
 * Shows relations with checkboxes, unresolved warnings.
 * FR-018
 */

import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import type { CampaignRelation, CatalogEntry } from '@roll1/shared';
import { Checkbox } from '../ui/checkbox';

interface RelationsReviewStepProps {
  relations: CampaignRelation[];
  catalogEntries: CatalogEntry[];
  onConfirm: (relations: CampaignRelation[]) => void;
  onSkip: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

const RELATION_LABELS: Record<string, string> = {
  location_link: 'Location link',
  journal_ref: 'Journal ref',
  note_ref: 'Note ref',
  npc_location: 'NPC at location',
};

interface RelationWithSelection extends CampaignRelation {
  selected: boolean;
  unresolved: boolean;
}

export function RelationsReviewStep({
  relations,
  catalogEntries,
  onConfirm,
  onSkip,
  onCancel,
  isLoading,
}: RelationsReviewStepProps) {
  const catalogNames = new Set(catalogEntries.map(e => e.name.toLowerCase().trim()));

  const [items, setItems] = useState<RelationWithSelection[]>(() =>
    relations.map(rel => ({
      ...rel,
      selected: true,
      unresolved:
        !catalogNames.has(rel.sourceName.toLowerCase().trim()) ||
        !catalogNames.has(rel.targetName.toLowerCase().trim()),
    })),
  );

  const handleToggle = useCallback((index: number) => {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, selected: !item.selected } : item,
    ));
  }, []);

  const selectedCount = items.filter(i => i.selected && !i.unresolved).length;
  const unresolvedCount = items.filter(i => i.unresolved).length;

  const handleConfirm = () => {
    const selected = items
      .filter(i => i.selected && !i.unresolved)
      .map(({ sourceName, targetName, relationType }) => ({ sourceName, targetName, relationType }));
    onConfirm(selected);
  };

  return (
    <div className="space-y-4">
      {/* [STYLE_TODO] Relations list styling */}
      <p className="text-ui font-medium">
        Найдено связей: {relations.length}
        {unresolvedCount > 0 && (
          <span className="text-warn ml-2">({unresolvedCount} неразрешённых)</span>
        )}
      </p>

      <div className="overflow-auto max-h-[300px] border rounded">
        <table className="w-full text-ui">
          <thead className="bg-muted sticky top-0">
            <tr>
              <th className="text-left px-2 py-1 text-meta w-8" />
              <th className="text-left px-2 py-1 text-meta text-muted-foreground">Источник</th>
              <th className="text-left px-2 py-1 text-meta text-muted-foreground" />
              <th className="text-left px-2 py-1 text-meta text-muted-foreground">Цель</th>
              <th className="text-left px-2 py-1 text-meta text-muted-foreground">Тип</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className={`border-t ${item.unresolved ? 'opacity-50' : ''} ${!item.selected ? 'opacity-40' : ''}`}
              >
                <td className="px-2 py-2">
                  <Checkbox
                    checked={item.selected}
                    disabled={item.unresolved}
                    onCheckedChange={() => handleToggle(index)}
                  />
                </td>
                <td className="px-2 py-2 max-w-[120px] truncate" title={item.sourceName}>
                  {item.sourceName}
                </td>
                <td className="px-2 py-2 text-muted-foreground text-meta">→</td>
                <td className="px-2 py-2 max-w-[120px] truncate" title={item.targetName}>
                  {item.targetName}
                </td>
                <td className="px-2 py-2">
                  <span className="text-meta px-1.5 py-0.5 rounded bg-muted">
                    {RELATION_LABELS[item.relationType] ?? item.relationType}
                  </span>
                  {item.unresolved && (
                    <span className="text-meta text-warn ml-1" title="Entity name not found in catalog">
                      ⚠ unresolved
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>Отмена</Button>
        <Button variant="outline" onClick={onSkip}>
          Пропустить связи
        </Button>
        <Button disabled={isLoading} onClick={handleConfirm}>
          {isLoading ? 'Применение...' : `Применить (${selectedCount} связей)`}
        </Button>
      </div>
    </div>
  );
}
